import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../../../core/apiClient'
import { Button } from '../../../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card'
import { Alert, AlertDescription } from '../../../../components/ui/alert'
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, Brain, LayoutList, RotateCcw, Lock } from 'lucide-react'

interface DraftNode {
  id: string
  type: 'domain' | 'section' | 'requirement' | 'sub_requirement'
  title: string
  description?: string
  fields?: { name: string; type: string; description?: string }[]
  children?: DraftNode[] | undefined
  confidence?: number
  accepted?: boolean
  locked?: boolean
  evidenceHints?: string[]
  sourceClause?: string
  sourcePage?: number
}

interface DraftResponse {
  draft_id: number
  framework_preview: DraftNode[]
  analysis_summary?: string
}

interface SavedDraftSummary {
  id: number
  standard_name?: string
  analysis_summary?: string
  status: string
  updated_at?: string
}

const toDraftResponse = (payload: any): DraftResponse => {
  let structure = payload?.framework_preview ?? payload?.structure_json ?? []
  if (typeof structure === 'string') {
    try {
      structure = JSON.parse(structure)
    } catch {
      structure = []
    }
  }

  return {
    draft_id: payload?.draft_id ?? payload?.id,
    framework_preview: Array.isArray(structure) ? structure : [],
    analysis_summary: payload?.analysis_summary
  }
}

interface AIFrameworkPreviewProps {
  initialDraft?: DraftResponse | null
}

const AIFrameworkPreview: React.FC<AIFrameworkPreviewProps> = ({ initialDraft }) => {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [draft, setDraft] = useState<DraftResponse | null>(initialDraft || null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState(false)
  const [showDiff, setShowDiff] = useState(false)
  const [editingNode, setEditingNode] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{ title: string; description: string }>({ title: '', description: '' })
  const [savedDrafts, setSavedDrafts] = useState<SavedDraftSummary[]>([])
  const [savedDraftsLoading, setSavedDraftsLoading] = useState(false)

  // Update draft when initialDraft changes
  useEffect(() => {
    if (initialDraft) {
      setDraft(initialDraft)
    }
  }, [initialDraft])

  const loadSavedDrafts = async () => {
    if (initialDraft) return

    setSavedDraftsLoading(true)
    try {
      const res = await apiClient.getInstance().get('/parser/ai/drafts?limit=10')
      setSavedDrafts(Array.isArray(res.data?.data) ? res.data.data : [])
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load saved drafts')
    } finally {
      setSavedDraftsLoading(false)
    }
  }

  useEffect(() => {
    loadSavedDrafts()
  }, [initialDraft])

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
    }
  }

  const uploadAndAnalyze = async () => {
    if (!file) return
    setLoading(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await apiClient.getInstance().post('/parser/ai/analyze-document', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setDraft(toDraftResponse(res.data.data))
      await loadSavedDrafts()
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to analyze')
    } finally {
      setLoading(false)
    }
  }

  const [generateInput, setGenerateInput] = useState('')
  const generateStandard = async () => {
    if (!generateInput.trim()) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.getInstance().post('/parser/ai/generate-standard', {
        description: generateInput,
        standardName: generateInput
      })
      setDraft(toDraftResponse(res.data.data))
      await loadSavedDrafts()
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to generate')
    } finally {
      setLoading(false)
    }
  }

  const openSavedDraft = async (draftId: number) => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.getInstance().get(`/parser/ai/draft/${draftId}`)
      setDraft(toDraftResponse(res.data?.data))
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load draft')
    } finally {
      setLoading(false)
    }
  }

  const finalize = async () => {
    if (!draft?.draft_id) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.getInstance().post(`/parser/ai/draft/${draft.draft_id}/finalize`, {
        frameworkName: 'AI Draft Framework'
      })
      const templateId = res.data?.data?.templateId
      if (templateId) navigate(`/compliance/definition/builder?templateId=${templateId}`)
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to finalize')
    } finally {
      setLoading(false)
    }
  }

  const updateDraftStructure = async (nextStructure: DraftNode[], correction?: any) => {
    if (!draft?.draft_id) return
    setUpdating(true)
    setError(null)
    try {
      await apiClient.getInstance().put(`/parser/ai/draft/${draft.draft_id}/node`, {
        structure: nextStructure,
        analysis: draft.analysis_summary,
        correction
      })
      setDraft({ ...draft, framework_preview: nextStructure })
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to update draft')
    } finally {
      setUpdating(false)
    }
  }

  const toggleAccept = (nodeId: string, current: DraftNode[], accept: boolean): DraftNode[] => {
    return current.map(n => ({
      ...n,
      accepted: n.id === nodeId ? accept : n.accepted,
      children: n.children ? toggleAccept(nodeId, n.children, accept) : undefined
    } as DraftNode))
  }

  const handleAccept = async (nodeId: string) => {
    if (!draft) return
    const updated = toggleAccept(nodeId, draft.framework_preview, true)
    await updateDraftStructure(updated, { nodeId, accepted: true })
  }

  const startEditing = (node: DraftNode) => {
    setEditingNode(node.id)
    setEditForm({ title: node.title, description: node.description || '' })
  }

  const saveEdit = async () => {
    if (!draft || !editingNode) return
    const updated = draft.framework_preview.map(n =>
      n.id === editingNode
        ? { ...n, title: editForm.title, description: editForm.description }
        : n
    )
    await updateDraftStructure(updated, { nodeId: editingNode, edited: true })
    setEditingNode(null)
  }

  const cancelEdit = () => {
    setEditingNode(null)
  }

  const renderNode = (node: DraftNode) => {
    const confidenceColor = node.confidence !== undefined
      ? node.confidence > 0.8 ? 'text-green-600'
        : node.confidence > 0.6 ? 'text-yellow-600'
        : 'text-red-600'
      : 'text-slate-500'

    const isEditing = editingNode === node.id

    return (
      <div key={node.id} className={`border rounded p-2 mb-2 ${node.locked ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase text-slate-500">{node.type}</span>
          {isEditing ? (
            <input
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              className="flex-1 px-2 py-1 border rounded text-sm"
              placeholder="Title"
            />
          ) : (
            <strong className={node.locked ? 'text-blue-900' : ''}>{node.title}</strong>
          )}
          {node.confidence !== undefined && (
            <span className={`text-xs ${confidenceColor} font-medium`}>
              {(node.confidence * 100).toFixed(0)}% confidence
            </span>
          )}
          {node.accepted && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          {node.locked && <Lock className="w-4 h-4 text-blue-600" />}
          <div className="ml-auto flex gap-2">
            {isEditing ? (
              <>
                <Button size="sm" variant="ghost" onClick={saveEdit} disabled={updating}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {!node.locked && (
                  <Button size="sm" variant="ghost" onClick={() => handleAccept(node.id)} disabled={updating}>
                    <CheckCircle2 className="w-4 h-4" /> Accept
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => startEditing(node)} disabled={updating || node.locked}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleRetry(node)} disabled={updating || node.locked}>
                  <RotateCcw className="w-4 h-4" /> Retry
                </Button>
              </>
            )}
          </div>
        </div>
        {isEditing ? (
          <textarea
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            className="w-full px-2 py-1 border rounded text-sm mt-1"
            placeholder="Description"
            rows={2}
          />
        ) : (
          node.description && <p className={`text-sm mt-1 ${node.locked ? 'text-blue-800' : 'text-slate-700'}`}>{node.description}</p>
        )}
        {node.sourceClause && <p className="text-xs text-slate-500">Clause {node.sourceClause}{node.sourcePage ? ` (p.${node.sourcePage})` : ''}</p>}
        {node.evidenceHints && node.evidenceHints.length > 0 && (
          <p className="text-xs text-slate-600">Evidence: {node.evidenceHints.join(', ')}</p>
        )}
        {node.fields && node.fields.length > 0 && (
          <div className="mt-1 text-xs text-slate-600">Fields: {node.fields.map(f => f.name).join(', ')}</div>
        )}
        {node.children && node.children.length > 0 && (
          <div className="ml-4 mt-2 border-l pl-2">
            {node.children.map(renderNode)}
          </div>
        )}
      </div>
    )
  }

  const handleRetry = async (node: DraftNode) => {
    if (!draft?.draft_id || !node.sourceClause) return
    setUpdating(true)
    setError(null)
    try {
      const res = await apiClient.getInstance().post(`/parser/ai/draft/${draft.draft_id}/retry-node`, {
        clauseId: node.sourceClause,
        title: node.title,
        text: node.description || node.title,
        page: node.sourcePage
      })
      if (res.data?.data?.framework_preview) {
        setDraft({ ...draft, framework_preview: res.data.data.framework_preview })
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Retry failed')
    } finally {
      setUpdating(false)
    }
  }

  // If we have an initial draft (from FrameworkParser), show only the preview
  if (initialDraft && draft) {
    return (
      <div className="space-y-4">
        {error && (
          <Alert variant="danger">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2"><LayoutList className="w-5 h-5" /> Preview Structure</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant={showDiff ? "primary" : "outline"} onClick={() => setShowDiff(!showDiff)}>
                  {showDiff ? "Hide Diff" : "Show Diff"}
                </Button>
                <Button onClick={finalize} variant="secondary" disabled={loading}>
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="ml-2">Finalize & Open Builder</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {showDiff && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Diff View:</strong> Changes from previous versions are highlighted. Green indicates additions, red indicates removals.
                </p>
              </div>
            )}
            <div className="space-y-2">
              {draft.framework_preview.map(renderNode)}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Otherwise, show the full interface with upload/generate options
  return (
    <div className="p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Brain className="w-5 h-5" /> AI Framework Preview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <input type="file" onChange={onFileChange} />
            <Button onClick={uploadAndAnalyze} disabled={loading || !file}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2">Analyze</span>
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="border rounded px-2 py-1 w-full"
              placeholder="Describe the standard (e.g., ISO 14067 carbon footprint)"
              value={generateInput}
              onChange={e => setGenerateInput(e.target.value)}
            />
            <Button onClick={generateStandard} disabled={loading || !generateInput.trim()}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
              <span className="ml-2">Generate</span>
            </Button>
            {draft && (
              <Button onClick={finalize} variant="secondary" disabled={loading}>
                <CheckCircle2 className="w-4 h-4" />
                <span className="ml-2">Finalize & Open Builder</span>
              </Button>
            )}
          </div>
          {error && (
            <Alert variant="danger">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <LayoutList className="w-5 h-5" /> Resume Saved Draft
            </CardTitle>
            <Button size="sm" variant="outline" onClick={loadSavedDrafts} disabled={savedDraftsLoading}>
              <RefreshCw className={`w-4 h-4 ${savedDraftsLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {savedDraftsLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading saved drafts...
            </div>
          ) : savedDrafts.length === 0 ? (
            <p className="text-sm text-slate-600">No saved drafts yet.</p>
          ) : (
            <div className="space-y-2">
              {savedDrafts.map((saved) => (
                <div key={saved.id} className="flex items-center justify-between border rounded p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{saved.standard_name || `Draft #${saved.id}`}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {saved.analysis_summary || 'No summary'}
                    </p>
                    <p className="text-xs text-slate-400">
                      Updated {saved.updated_at ? new Date(saved.updated_at).toLocaleString() : 'unknown'}
                    </p>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => openSavedDraft(saved.id)} disabled={loading}>
                    Open
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {draft && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2"><LayoutList className="w-5 h-5" /> Preview Structure</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant={showDiff ? "primary" : "outline"} onClick={() => setShowDiff(!showDiff)}>
                  {showDiff ? "Hide Diff" : "Show Diff"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {showDiff && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Diff View:</strong> Changes from previous versions are highlighted. Green indicates additions, red indicates removals.
                </p>
              </div>
            )}
            {draft.framework_preview.map(renderNode)}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default AIFrameworkPreview
